import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Plus, Send } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  RowActions,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Application = {
  id: number
  person: number
  person_name: string
  person_email: string
  job: number
  job_code: string
  job_title: string
  stage: number
  stage_name: string
  stage_is_won: boolean
  stage_is_lost: boolean
  status: 'ACTIVE' | 'HIRED' | 'REJECTED' | 'WITHDRAWN'
  source: string
  applied_at: string
  resume_url: string
  cover_letter: string
  rejection_reason: string
  rejection_category: string
  hired_employment: number | null
  hired_employment_number: string | null
  consent_at: string | null
  retention_until: string | null
  notes: string
}

type StageLite = {
  id: number
  name: string
  is_won: boolean
  is_lost: boolean
  pipeline: number
}

type Activity = {
  id: number
  application: number
  type:
    | 'CREATED' | 'NOTE' | 'STAGE_CHANGED' | 'STATUS_CHANGED'
    | 'INTERVIEW' | 'SCORECARD' | 'OFFER' | 'HIRED' | 'REJECTED'
  body: string
  performed_by: number | null
  performed_by_username: string | null
  automatic: boolean
  timestamp: string
}

const STATUS_COLOR: Record<Application['status'], string> = {
  ACTIVE: 'bg-blue-100 text-blue-700',
  HIRED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  WITHDRAWN: 'bg-gray-100 text-gray-700',
}

const REJECTION_LABEL: Record<string, string> = {
  COMP: 'Compensation mismatch',
  FIT: 'Cultural / team fit',
  EXPERIENCE: 'Experience / skills gap',
  LOCATION: 'Location',
  TIMING: 'Timing',
  COUNTEROFFER: 'Counteroffer from current employer',
  OTHER: 'Other',
}

const ACTIVITY_LABEL: Record<Activity['type'], string> = {
  CREATED: 'Application created',
  NOTE: 'Note',
  STAGE_CHANGED: 'Stage changed',
  STATUS_CHANGED: 'Status changed',
  INTERVIEW: 'Interview',
  SCORECARD: 'Scorecard',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
}

const ACTIVITY_COLOR: Record<Activity['type'], string> = {
  CREATED: 'bg-slate-100 text-slate-700',
  NOTE: 'bg-blue-100 text-blue-700',
  STAGE_CHANGED: 'bg-indigo-100 text-indigo-700',
  STATUS_CHANGED: 'bg-violet-100 text-violet-700',
  INTERVIEW: 'bg-amber-100 text-amber-700',
  SCORECARD: 'bg-amber-100 text-amber-700',
  OFFER: 'bg-cyan-100 text-cyan-700',
  HIRED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
}

type TabKey = 'details' | 'activity' | 'interviews' | 'scorecards' | 'offer' | 'files'

// ─────────────────── Page ───────────────────
export default function ATSApplicationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const applicationId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const appQ = useQuery({
    queryKey: ['ats', 'application', applicationId],
    queryFn: async () => (await api.get<Application>(`/ats/applications/${applicationId}/`)).data,
    enabled: !!applicationId,
  })

  // Fetch all stages for this job's pipeline (for the stage picker).
  const stagesQ = useQuery({
    queryKey: ['ats', 'stages', { application: applicationId }],
    queryFn: async () => {
      const app = appQ.data
      if (!app) return []
      const job = (await api.get(`/ats/jobs/${app.job}/`)).data
      if (!job.pipeline) return []
      return (await api.get<StageLite[]>(`/ats/stages/?pipeline=${job.pipeline}`)).data
    },
    enabled: !!appQ.data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Application>) =>
      (await api.patch(`/ats/applications/${applicationId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'application', applicationId] })
      qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: applicationId }] })
      qc.invalidateQueries({ queryKey: ['ats', 'kanban'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/ats/applications/${applicationId}/`),
    onSuccess: () => {
      toast.success('Application deleted')
      if (appQ.data?.job) navigate(`/admin/ats/jobs/${appQ.data.job}`)
      else navigate('/admin/ats/jobs')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (appQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (appQ.isError || !appQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Application not found.</p>
          <Link to="/admin/ats/jobs" className="text-blue-600 hover:underline">← Back to jobs</Link>
        </div>
      </AdminLayout>
    )
  }

  const app = appQ.data
  const stages = stagesQ.data || []
  const save = (payload: Partial<Application>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to={`/admin/ats/jobs/${app.job}`} className="text-muted-foreground hover:text-foreground hover:underline">
            ← {app.job_code} {app.job_title}
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">
              <Link to={`/admin/hr/people/${app.person}`} className="hover:underline">
                {app.person_name}
              </Link>
            </h2>
            <div className="text-sm text-muted-foreground mt-0.5">
              {app.person_email && <span>{app.person_email} · </span>}
              Stage: <span className="font-medium">{app.stage_name}</span>
              {app.source && <span> · Source: {app.source}</span>}
              {app.applied_at && <span> · Applied {new Date(app.applied_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={app.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'HIRED', label: 'Hired' },
                { value: 'REJECTED', label: 'Rejected' },
                { value: 'WITHDRAWN', label: 'Withdrawn' },
              ]}
              onSave={(v) => save({ status: v as Application['status'] })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`application from ${app.person_name}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete application</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'activity', 'interviews', 'scorecards', 'offer', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'activity' && 'Activity'}
              {t === 'interviews' && 'Interviews'}
              {t === 'scorecards' && 'Scorecards'}
              {t === 'offer' && 'Offer'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab app={app} stages={stages} onSave={save} />}
      {tab === 'activity' && <ActivityTab applicationId={applicationId} />}
      {tab === 'interviews' && <InterviewsTab applicationId={applicationId} candidateName={app.person_name} />}
      {tab === 'scorecards' && <ScorecardsTab applicationId={applicationId} />}
      {tab === 'offer' && <OfferTab applicationId={applicationId} candidateName={app.person_name} />}
      {tab === 'files' && <FilesTab parent="ats.application" parentId={applicationId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  app, stages, onSave,
}: {
  app: Application
  stages: StageLite[]
  onSave: (payload: Partial<Application>) => Promise<void>
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Application">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Stage">
            <InlineSelect
              value={String(app.stage)}
              displayLabel={app.stage_name}
              options={stages.map((s) => ({
                value: String(s.id),
                label: s.name + (s.is_won ? ' (won)' : s.is_lost ? ' (lost)' : ''),
                searchText: s.name,
              }))}
              onSave={(v) => onSave({ stage: Number(v) })}
              helper="Moving into a Won/Lost stage will flip the application's status accordingly."
            />
          </Row>
          <Row label="Source">
            <InlineText
              value={app.source}
              onSave={(v) => onSave({ source: v })}
              placeholder="LinkedIn, Referral, Direct…"
            />
          </Row>
          <Row label="Resume URL">
            <InlineText
              value={app.resume_url}
              onSave={(v) => onSave({ resume_url: v })}
              placeholder="Drive / Dropbox / S3 link"
            />
          </Row>
          {app.resume_url && (
            <Row label=" ">
              <a href={app.resume_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline">
                Open resume in new tab →
              </a>
            </Row>
          )}
          {app.hired_employment && (
            <Row label="Hired as">
              <Link to={`/admin/hr/employments/${app.hired_employment}`}
                className="text-blue-600 hover:underline">
                Employment {app.hired_employment_number}
              </Link>
            </Row>
          )}
        </dl>
      </Card>

      <Card title="GDPR & consent">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Consented at">
            <span>{app.consent_at ? new Date(app.consent_at).toLocaleString() : <Dash />}</span>
          </Row>
          <Row label="Retain until">
            <span>{app.retention_until || <Dash />}</span>
          </Row>
        </dl>
        <p className="text-xs text-muted-foreground mt-3">
          GDPR retention controls. Hook up consent capture in Phase 7i (career page).
        </p>
      </Card>

      <Card title="Cover letter" className="lg:col-span-2">
        <InlineText
          value={app.cover_letter}
          multiline
          onSave={(v) => onSave({ cover_letter: v })}
          placeholder="Candidate's cover letter."
        />
      </Card>

      <Card title="Rejection (if applicable)" className="lg:col-span-2">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Category">
            <InlineSelect
              value={app.rejection_category || ''}
              displayLabel={app.rejection_category ? REJECTION_LABEL[app.rejection_category] : <Dash />}
              allowClear
              options={Object.entries(REJECTION_LABEL).map(([k, v]) => ({ value: k, label: v }))}
              onSave={(v) => onSave({ rejection_category: v })}
            />
          </Row>
          <Row label="Reason">
            <InlineText
              value={app.rejection_reason}
              multiline
              onSave={(v) => onSave({ rejection_reason: v })}
              placeholder="Free-text reason; informs source / process analytics."
            />
          </Row>
        </dl>
      </Card>

      <Card title="Internal notes" className="lg:col-span-2">
        <InlineText
          value={app.notes}
          multiline
          onSave={(v) => onSave({ notes: v })}
          placeholder="Recruiter scratch pad — quick context not worth a timeline note."
        />
      </Card>
    </div>
  )
}

// ─────────────────── Activity tab ───────────────────

function ActivityTab({ applicationId }: { applicationId: number }) {
  const qc = useQueryClient()
  const activitiesQ = useQuery({
    queryKey: ['ats', 'activities', { application: applicationId }],
    queryFn: async () =>
      (await api.get<Activity[]>(`/ats/activities/?application=${applicationId}`)).data,
  })

  const [noteText, setNoteText] = useState('')

  const noteMut = useMutation({
    mutationFn: async () => (await api.post('/ats/activities/', {
      application: applicationId,
      type: 'NOTE',
      body: noteText.trim(),
    })).data,
    onSuccess: () => {
      toast.success('Note added')
      setNoteText('')
      qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: applicationId }] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const activities = activitiesQ.data || []

  function submitNote() {
    if (!noteText.trim()) return
    noteMut.mutate()
  }

  return (
    <div className="space-y-4">
      {/* New note input */}
      <div className="bg-card rounded-lg shadow p-4">
        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Add a note
        </label>
        <Textarea
          rows={3}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Phone screen feedback, hiring manager comments, follow-up reminders…"
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-muted-foreground">
            Stage changes are auto-logged. Use notes for context the timeline doesn't capture.
          </p>
          <Button
            type="button"
            size="sm"
            onClick={submitNote}
            disabled={!noteText.trim() || noteMut.isPending}
          >
            <Send className="h-3 w-3 mr-1" /> {noteMut.isPending ? 'Saving…' : 'Post note'}
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {activitiesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : activities.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-6 text-center text-muted-foreground text-sm">
          No activity yet.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <ul className="divide-y divide-border">
            {activities.map((a) => (
              <li key={a.id} className="px-4 py-3">
                <div className="flex items-start gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${ACTIVITY_COLOR[a.type]}`}>
                    {ACTIVITY_LABEL[a.type]}
                  </span>
                  <div className="flex-1 min-w-0">
                    {a.body && (
                      <div className="text-sm text-foreground whitespace-pre-wrap">{a.body}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {a.performed_by_username || 'system'}
                      {' · '}
                      {new Date(a.timestamp).toLocaleString()}
                      {a.automatic && <span className="ml-2 italic">auto</span>}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ─────────────────── Interviews tab ───────────────────

type Interview = {
  id: number
  application: number
  scheduled_at: string
  duration_minutes: number
  format: 'PHONE' | 'VIDEO' | 'ONSITE'
  meeting_url: string
  location: string
  notes: string
  status: 'SCHEDULED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED'
  participants: { id: number; person: number; person_name: string; role: 'HIRING_MANAGER' | 'INTERVIEWER' | 'OBSERVER' }[]
  scorecard_count: number
  pending_scorecards: number
}

type PersonLite = { id: number; full_name: string }

type Scorecard = {
  id: number
  interview: number
  application: number
  interviewer: number
  interviewer_name: string
  overall_rating: number
  recommendation: 'STRONG_HIRE' | 'HIRE' | 'NO_HIRE' | 'STRONG_NO_HIRE'
  strengths: string
  concerns: string
  competency_ratings: Record<string, number>
  submitted_at: string
}

const FORMAT_LABEL: Record<Interview['format'], string> = {
  PHONE: 'Phone', VIDEO: 'Video', ONSITE: 'On-site',
}

const INTERVIEW_STATUS_COLOR: Record<Interview['status'], string> = {
  SCHEDULED: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-800',
  NO_SHOW: 'bg-amber-100 text-amber-800',
  CANCELLED: 'bg-gray-100 text-gray-700',
}

const ROLE_LABEL: Record<Interview['participants'][number]['role'], string> = {
  HIRING_MANAGER: 'Hiring Manager',
  INTERVIEWER: 'Interviewer',
  OBSERVER: 'Observer',
}

const RECOMMENDATION_LABEL: Record<Scorecard['recommendation'], string> = {
  STRONG_HIRE: 'Strong hire',
  HIRE: 'Hire',
  NO_HIRE: 'No hire',
  STRONG_NO_HIRE: 'Strong no hire',
}

const RECOMMENDATION_COLOR: Record<Scorecard['recommendation'], string> = {
  STRONG_HIRE: 'bg-green-100 text-green-800',
  HIRE: 'bg-emerald-100 text-emerald-700',
  NO_HIRE: 'bg-red-100 text-red-700',
  STRONG_NO_HIRE: 'bg-rose-100 text-rose-700',
}

const DEFAULT_COMPETENCIES = [
  { code: 'technical', label: 'Technical / functional skills' },
  { code: 'communication', label: 'Communication' },
  { code: 'problem_solving', label: 'Problem solving' },
  { code: 'cultural_fit', label: 'Cultural fit' },
]

function InterviewsTab({ applicationId, candidateName }: { applicationId: number; candidateName: string }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const interviewsQ = useQuery({
    queryKey: ['ats', 'interviews', { application: applicationId }],
    queryFn: async () =>
      (await api.get<Interview[]>(`/ats/interviews/?application=${applicationId}`)).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonLite[]>('/hr/persons/')).data,
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Interview | null>(null)

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ats/interviews/${id}/`),
    onSuccess: () => {
      toast.success('Interview deleted')
      qc.invalidateQueries({ queryKey: ['ats', 'interviews', { application: applicationId }] })
      qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: applicationId }] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const interviews = interviewsQ.data || []
  const persons = personsQ.data || []

  function openCreate() { setEditing(null); setDrawerOpen(true) }
  function openEdit(iv: Interview) { setEditing(iv); setDrawerOpen(true) }

  return (
    <div>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Schedule interviews and track who's giving feedback. Each non-observer participant
          should fill a scorecard on the Scorecards tab.
        </p>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Schedule interview</Button>
      </div>

      {interviewsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : interviews.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          No interviews scheduled yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {interviews.map((iv) => (
            <li key={iv.id} className="bg-card rounded-lg shadow p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">
                      {new Date(iv.scheduled_at).toLocaleString()}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded font-semibold bg-slate-100 text-slate-700">
                      {FORMAT_LABEL[iv.format]}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${INTERVIEW_STATUS_COLOR[iv.status]}`}>
                      {iv.status}
                    </span>
                    <span className="text-xs text-muted-foreground">{iv.duration_minutes} min</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {iv.participants.length === 0 ? (
                      <span>No participants assigned.</span>
                    ) : (
                      iv.participants.map((p, i) => (
                        <span key={p.id}>
                          {i > 0 && <span> · </span>}
                          {p.person_name} <span className="opacity-70">({ROLE_LABEL[p.role]})</span>
                        </span>
                      ))
                    )}
                  </div>
                  {iv.meeting_url && (
                    <div className="text-xs mt-1">
                      <a href={iv.meeting_url} target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 hover:underline">
                        Open meeting link →
                      </a>
                    </div>
                  )}
                  {iv.location && (
                    <div className="text-xs text-muted-foreground mt-1">📍 {iv.location}</div>
                  )}
                  {iv.notes && (
                    <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap border-l-2 border-border pl-2">
                      {iv.notes}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mt-2">
                    {iv.scorecard_count} scorecard{iv.scorecard_count === 1 ? '' : 's'} submitted
                    {iv.pending_scorecards > 0 && (
                      <span className="text-amber-700 font-semibold ml-1">
                        · {iv.pending_scorecards} pending
                      </span>
                    )}
                  </div>
                </div>
                <RowActions
                  onEdit={() => openEdit(iv)}
                  onDelete={() => confirm(
                    `interview on ${new Date(iv.scheduled_at).toLocaleString()}`,
                    () => deleteMut.mutate(iv.id),
                  )}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <InterviewDrawer
        open={drawerOpen}
        onOpenChange={(v) => { setDrawerOpen(v); if (!v) setEditing(null) }}
        applicationId={applicationId}
        candidateName={candidateName}
        editing={editing}
        persons={persons}
      />
    </div>
  )
}

function InterviewDrawer({
  open, onOpenChange, applicationId, candidateName, editing, persons,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  applicationId: number
  candidateName: string
  editing: Interview | null
  persons: PersonLite[]
}) {
  const qc = useQueryClient()

  const initial = () => editing
    ? {
        scheduled_at: editing.scheduled_at.slice(0, 16),  // datetime-local format
        duration_minutes: editing.duration_minutes,
        format: editing.format,
        meeting_url: editing.meeting_url,
        location: editing.location,
        notes: editing.notes,
        status: editing.status,
        participants: editing.participants.map((p) => ({ person: p.person, role: p.role })),
      }
    : {
        scheduled_at: defaultScheduledAt(),
        duration_minutes: 60,
        format: 'VIDEO' as Interview['format'],
        meeting_url: '',
        location: '',
        notes: '',
        status: 'SCHEDULED' as Interview['status'],
        participants: [] as { person: number; role: Interview['participants'][number]['role'] }[],
      }

  const [form, setForm] = useState(initial())
  useEffect(() => { if (open) setForm(initial()) /* eslint-disable-line */ }, [open, editing])

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        application: applicationId,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 60,
        format: form.format,
        meeting_url: form.meeting_url,
        location: form.location,
        notes: form.notes,
        status: form.status,
        participants: form.participants,
      }
      if (editing) {
        return (await api.patch(`/ats/interviews/${editing.id}/`, payload)).data
      }
      return (await api.post('/ats/interviews/', payload)).data
    },
    onSuccess: () => {
      toast.success(editing ? 'Interview updated' : 'Interview scheduled')
      qc.invalidateQueries({ queryKey: ['ats', 'interviews', { application: applicationId }] })
      qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: applicationId }] })
      onOpenChange(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function addParticipant() {
    setForm((f) => ({
      ...f,
      participants: [...f.participants, { person: 0, role: 'INTERVIEWER' }],
    }))
  }
  function updateParticipant(idx: number, patch: Partial<typeof form.participants[number]>) {
    setForm((f) => ({
      ...f,
      participants: f.participants.map((p, i) => i === idx ? { ...p, ...patch } : p),
    }))
  }
  function removeParticipant(idx: number) {
    setForm((f) => ({ ...f, participants: f.participants.filter((_, i) => i !== idx) }))
  }

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit interview' : `Schedule interview for ${candidateName}`}
      description="Pick a time, format, and the interviewers / observers. Calendar invites are sent externally — paste a meeting link in for VIDEO interviews."
      onSubmit={() => saveMut.mutate()}
      isPending={saveMut.isPending}
      submitLabel={editing ? 'Save' : 'Schedule'}
    >
      <FieldGroup>
        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="iv-when">When *</FieldLabel>
            <Input id="iv-when" type="datetime-local" required
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="iv-dur">Duration (min)</FieldLabel>
            <Input id="iv-dur" type="number" min={5} step={5}
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: Math.max(5, Number(e.target.value) || 60) })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="iv-fmt">Format</FieldLabel>
            <Select value={form.format}
              onValueChange={(v) => setForm({ ...form, format: v as Interview['format'] })}>
              <SelectTrigger id="iv-fmt"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(FORMAT_LABEL) as Interview['format'][]).map((k) => (
                  <SelectItem key={k} value={k}>{FORMAT_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="iv-status">Status</FieldLabel>
            <Select value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v as Interview['status'] })}>
              <SelectTrigger id="iv-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="NO_SHOW">No-show</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {form.format === 'VIDEO' && (
          <Field>
            <FieldLabel htmlFor="iv-url">Meeting URL</FieldLabel>
            <Input id="iv-url" type="url" value={form.meeting_url}
              onChange={(e) => setForm({ ...form, meeting_url: e.target.value })}
              placeholder="https://meet.google.com/…" />
          </Field>
        )}
        {form.format === 'ONSITE' && (
          <Field>
            <FieldLabel htmlFor="iv-loc">Location</FieldLabel>
            <Input id="iv-loc" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Office, Room 4B" />
          </Field>
        )}

        <Field>
          <div className="flex items-center justify-between mb-1">
            <FieldLabel htmlFor="iv-parts">Participants</FieldLabel>
            <button type="button" onClick={addParticipant}
              className="text-xs text-blue-600 hover:underline">
              + Add participant
            </button>
          </div>
          {form.participants.length === 0 ? (
            <p className="text-xs text-muted-foreground">No participants yet — add at least one interviewer.</p>
          ) : (
            <div className="space-y-2">
              {form.participants.map((p, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <SearchableSelect
                    className="flex-1"
                    value={p.person ? String(p.person) : NONE}
                    onChange={(v) => updateParticipant(idx, { person: v === NONE ? 0 : Number(v) })}
                    placeholder="— pick —"
                    options={[
                      { value: NONE, label: '— pick —' },
                      ...persons.map((per) => ({ value: String(per.id), label: per.full_name })),
                    ]}
                  />
                  <Select value={p.role}
                    onValueChange={(v) => updateParticipant(idx, { role: v as typeof p.role })}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HIRING_MANAGER">Hiring Manager</SelectItem>
                      <SelectItem value="INTERVIEWER">Interviewer</SelectItem>
                      <SelectItem value="OBSERVER">Observer</SelectItem>
                    </SelectContent>
                  </Select>
                  <button type="button" onClick={() => removeParticipant(idx)}
                    className="text-xs text-red-600 hover:underline">
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <FieldDescription>
            Observers don't get a pending scorecard. Interviewers + Hiring Managers do.
          </FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="iv-notes">Briefing notes</FieldLabel>
          <Textarea id="iv-notes" rows={3} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="What to focus on, questions to cover, candidate context." />
          <FieldDescription>Visible to interviewers, not to the candidate.</FieldDescription>
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

function defaultScheduledAt() {
  // Round to the next half-hour, 24h from now.
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60)
  d.setSeconds(0); d.setMilliseconds(0)
  // datetime-local needs "YYYY-MM-DDTHH:MM" in local time.
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ─────────────────── Scorecards tab ───────────────────

function ScorecardsTab({ applicationId }: { applicationId: number }) {
  const qc = useQueryClient()
  const interviewsQ = useQuery({
    queryKey: ['ats', 'interviews', { application: applicationId }],
    queryFn: async () =>
      (await api.get<Interview[]>(`/ats/interviews/?application=${applicationId}`)).data,
  })
  const scorecardsQ = useQuery({
    queryKey: ['ats', 'scorecards', { application: applicationId }],
    queryFn: async () =>
      (await api.get<Scorecard[]>(`/ats/scorecards/?application=${applicationId}`)).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonLite[]>('/hr/persons/')).data,
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerCtx, setDrawerCtx] = useState<{ interview: Interview; person: PersonLite } | null>(null)

  function openScorecard(interview: Interview, person: PersonLite) {
    setDrawerCtx({ interview, person })
    setDrawerOpen(true)
  }

  const interviews = interviewsQ.data || []
  const scorecards = scorecardsQ.data || []
  const persons = personsQ.data || []

  // Summary stats across all scorecards
  const summary = (() => {
    if (scorecards.length === 0) return null
    const avg = scorecards.reduce((s, c) => s + c.overall_rating, 0) / scorecards.length
    const byRec: Record<string, number> = {}
    for (const c of scorecards) byRec[c.recommendation] = (byRec[c.recommendation] || 0) + 1
    return { avg, byRec }
  })()

  if (interviewsQ.isLoading || scorecardsQ.isLoading) {
    return <p className="text-muted-foreground">Loading…</p>
  }

  return (
    <div>
      {summary && (
        <div className="bg-card rounded-lg shadow p-4 mb-4 flex items-center gap-6">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Average rating</div>
            <div className="text-2xl font-bold">{summary.avg.toFixed(1)} <span className="text-sm text-muted-foreground">/ 5</span></div>
          </div>
          <div className="flex-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Recommendations</div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(RECOMMENDATION_LABEL) as Scorecard['recommendation'][]).map((k) => (
                summary.byRec[k] ? (
                  <span key={k}
                    className={`text-xs px-2 py-0.5 rounded font-semibold ${RECOMMENDATION_COLOR[k]}`}>
                    {RECOMMENDATION_LABEL[k]} × {summary.byRec[k]}
                  </span>
                ) : null
              ))}
            </div>
          </div>
        </div>
      )}

      {interviews.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          No interviews scheduled yet — go to the Interviews tab to schedule one.
        </div>
      ) : (
        <div className="space-y-4">
          {interviews.map((iv) => {
            const ivScorecards = scorecards.filter((s) => s.interview === iv.id)
            const submittedIds = new Set(ivScorecards.map((s) => s.interviewer))
            const expectedParticipants = iv.participants.filter((p) => p.role !== 'OBSERVER')
            return (
              <div key={iv.id} className="bg-card rounded-lg shadow overflow-hidden">
                <div className="px-4 py-2 border-b bg-muted/40 flex items-center justify-between">
                  <div className="text-sm font-semibold">
                    {new Date(iv.scheduled_at).toLocaleString()} · {FORMAT_LABEL[iv.format]}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {ivScorecards.length}/{expectedParticipants.length} submitted
                  </div>
                </div>
                <ul className="divide-y divide-border">
                  {expectedParticipants.length === 0 && (
                    <li className="px-4 py-3 text-xs text-muted-foreground italic">
                      No interviewers assigned.
                    </li>
                  )}
                  {expectedParticipants.map((p) => {
                    const sc = ivScorecards.find((s) => s.interviewer === p.person)
                    if (sc) return <ScorecardRow key={p.id} sc={sc} role={p.role} />
                    return (
                      <li key={p.id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">{p.person_name}</div>
                          <div className="text-xs text-muted-foreground">{ROLE_LABEL[p.role]} · pending</div>
                        </div>
                        <Button size="sm" variant="outline"
                          onClick={() => {
                            const person = persons.find((pp) => pp.id === p.person)
                            if (person) openScorecard(iv, person)
                          }}
                        >
                          Submit scorecard
                        </Button>
                      </li>
                    )
                  })}
                </ul>
                <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground">
                  {submittedIds.size < expectedParticipants.length
                    ? `${expectedParticipants.length - submittedIds.size} pending`
                    : 'All scorecards in'}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <ScorecardDrawer
        open={drawerOpen}
        onOpenChange={(v) => { setDrawerOpen(v); if (!v) setDrawerCtx(null) }}
        ctx={drawerCtx}
        applicationId={applicationId}
        onSubmitted={() => {
          qc.invalidateQueries({ queryKey: ['ats', 'scorecards', { application: applicationId }] })
          qc.invalidateQueries({ queryKey: ['ats', 'interviews', { application: applicationId }] })
          qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: applicationId }] })
        }}
      />
    </div>
  )
}

function ScorecardRow({ sc, role }: { sc: Scorecard; role: Interview['participants'][number]['role'] }) {
  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{sc.interviewer_name}</span>
            <span className="text-xs text-muted-foreground">{ROLE_LABEL[role]}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${RECOMMENDATION_COLOR[sc.recommendation]}`}>
              {RECOMMENDATION_LABEL[sc.recommendation]}
            </span>
            <span className="text-xs font-mono">{sc.overall_rating}/5</span>
          </div>
          {sc.strengths && (
            <div className="text-xs mt-1">
              <span className="font-semibold text-green-700">Strengths:</span>{' '}
              <span className="text-foreground whitespace-pre-wrap">{sc.strengths}</span>
            </div>
          )}
          {sc.concerns && (
            <div className="text-xs mt-1">
              <span className="font-semibold text-red-700">Concerns:</span>{' '}
              <span className="text-foreground whitespace-pre-wrap">{sc.concerns}</span>
            </div>
          )}
          {Object.keys(sc.competency_ratings || {}).length > 0 && (
            <div className="text-xs mt-1 flex flex-wrap gap-2">
              {Object.entries(sc.competency_ratings).map(([k, v]) => (
                <span key={k} className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  {k}: <span className="font-mono font-semibold">{v}</span>
                </span>
              ))}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">
            Submitted {new Date(sc.submitted_at).toLocaleString()}
          </div>
        </div>
      </div>
    </li>
  )
}

function ScorecardDrawer({
  open, onOpenChange, ctx, applicationId, onSubmitted,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  ctx: { interview: Interview; person: PersonLite } | null
  applicationId: number
  onSubmitted: () => void
}) {
  const [form, setForm] = useState({
    overall_rating: 3,
    recommendation: 'HIRE' as Scorecard['recommendation'],
    strengths: '',
    concerns: '',
    competency_ratings: {} as Record<string, number>,
  })

  useEffect(() => {
    if (open) {
      setForm({
        overall_rating: 3,
        recommendation: 'HIRE',
        strengths: '',
        concerns: '',
        competency_ratings: {},
      })
    }
  }, [open, ctx])

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!ctx) throw new Error('No interview / person selected')
      return (await api.post('/ats/scorecards/', {
        interview: ctx.interview.id,
        interviewer: ctx.person.id,
        overall_rating: form.overall_rating,
        recommendation: form.recommendation,
        strengths: form.strengths,
        concerns: form.concerns,
        competency_ratings: form.competency_ratings,
      })).data
    },
    onSuccess: () => {
      toast.success('Scorecard submitted')
      onSubmitted()
      onOpenChange(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function setCompetency(code: string, rating: number) {
    setForm((f) => ({
      ...f,
      competency_ratings: { ...f.competency_ratings, [code]: rating },
    }))
  }

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={ctx ? `Scorecard — ${ctx.person.full_name}` : 'Scorecard'}
      description="Structured feedback. Once submitted the scorecard is visible to the hiring panel; in v1 there's no draft state."
      onSubmit={() => saveMut.mutate()}
      isPending={saveMut.isPending}
      submitLabel="Submit"
    >
      <FieldGroup>
        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="sc-rating">Overall rating *</FieldLabel>
            <Select value={String(form.overall_rating)}
              onValueChange={(v) => setForm({ ...form, overall_rating: Number(v) })}>
              <SelectTrigger id="sc-rating"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} / 5</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="sc-rec">Recommendation *</FieldLabel>
            <Select value={form.recommendation}
              onValueChange={(v) => setForm({ ...form, recommendation: v as Scorecard['recommendation'] })}>
              <SelectTrigger id="sc-rec"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(RECOMMENDATION_LABEL) as Scorecard['recommendation'][]).map((k) => (
                  <SelectItem key={k} value={k}>{RECOMMENDATION_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="sc-strengths">Strengths</FieldLabel>
          <Textarea id="sc-strengths" rows={3} value={form.strengths}
            onChange={(e) => setForm({ ...form, strengths: e.target.value })}
            placeholder="What did the candidate do well?" />
        </Field>
        <Field>
          <FieldLabel htmlFor="sc-concerns">Concerns</FieldLabel>
          <Textarea id="sc-concerns" rows={3} value={form.concerns}
            onChange={(e) => setForm({ ...form, concerns: e.target.value })}
            placeholder="Reservations, gaps, follow-up questions." />
        </Field>

        <Field>
          <FieldLabel>Competency ratings (optional)</FieldLabel>
          <div className="space-y-2 mt-1">
            {DEFAULT_COMPETENCIES.map((c) => (
              <div key={c.code} className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-muted-foreground">{c.label}</span>
                <Select
                  value={form.competency_ratings[c.code] ? String(form.competency_ratings[c.code]) : NONE}
                  onValueChange={(v) => {
                    if (v === NONE) {
                      const next = { ...form.competency_ratings }
                      delete next[c.code]
                      setForm({ ...form, competency_ratings: next })
                    } else {
                      setCompetency(c.code, Number(v))
                    }
                  }}
                >
                  <SelectTrigger className="w-32"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>—</SelectItem>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <FieldDescription>Skip any competency that isn't relevant for this interview.</FieldDescription>
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

// ─────────────────── Offer tab ───────────────────

type Offer = {
  id: number
  application: number
  position: number | null
  position_code: string | null
  position_title: string | null
  salary_amount: string
  currency_code: string
  salary_frequency: 'ANNUAL' | 'MONTHLY' | 'DAILY' | 'HOURLY'
  signing_bonus: string | null
  start_date: string
  response_deadline: string | null
  other_terms: string
  letter_url: string
  status: 'DRAFT' | 'APPROVED' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'WITHDRAWN'
  sent_at: string | null
  responded_at: string | null
  approved_by_username: string | null
  approved_at: string | null
  is_locked: boolean
}

const OFFER_STATUS_COLOR: Record<Offer['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  SENT: 'bg-amber-100 text-amber-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-slate-100 text-slate-700',
  WITHDRAWN: 'bg-rose-100 text-rose-700',
}

const OFFER_STATUS_LABEL: Record<Offer['status'], string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  SENT: 'Sent',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
}

const FREQUENCY_LABEL: Record<Offer['salary_frequency'], string> = {
  ANNUAL: 'Annual',
  MONTHLY: 'Monthly',
  DAILY: 'Daily',
  HOURLY: 'Hourly',
}

function OfferTab({ applicationId, candidateName }: { applicationId: number; candidateName: string }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me/')).data,
    staleTime: 60 * 1000,
  })
  const offerQ = useQuery({
    queryKey: ['ats', 'offer', { application: applicationId }],
    queryFn: async () => {
      const list = (await api.get<Offer[]>(`/ats/offers/?application=${applicationId}`)).data
      return list.length > 0 ? list[0] : null
    },
  })
  const appQ = useQuery({
    queryKey: ['ats', 'application', applicationId],
    queryFn: async () => (await api.get<Application>(`/ats/applications/${applicationId}/`)).data,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ats', 'offer', { application: applicationId }] })
    qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: applicationId }] })
    qc.invalidateQueries({ queryKey: ['ats', 'application', applicationId] })
    qc.invalidateQueries({ queryKey: ['ats', 'jobs'] })
  }

  const offer = offerQ.data
  const application = appQ.data
  const canApprove = !!meQ.data?.permissions?.includes('ats.approve_offer') || meQ.data?.is_superuser
  const canHire = !!meQ.data?.permissions?.includes('ats.hire') || meQ.data?.is_superuser

  const convertMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/ats/applications/${applicationId}/convert-to-employment/`)).data,
    onSuccess: (updated: any) => {
      toast.success('Candidate hired — Employment created')
      invalidate()
      if (updated?.hired_employment) {
        navigate(`/admin/hr/employments/${updated.hired_employment}`)
      }
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  if (offerQ.isLoading) return <p className="text-muted-foreground">Loading…</p>

  if (!offer) {
    return (
      <div>
        {dialog}
        <NoOfferYet
          applicationId={applicationId}
          candidateName={candidateName}
          onCreated={invalidate}
        />
      </div>
    )
  }

  const alreadyHired = !!application?.hired_employment

  return (
    <div className="space-y-4">
      {dialog}
      {alreadyHired && (
        <div className="bg-green-50 border border-green-200 text-green-900 rounded-md p-4 text-sm flex items-center justify-between gap-4">
          <div>
            <span className="font-semibold">Hired.</span> Employment record created:{' '}
            <Link to={`/admin/hr/employments/${application!.hired_employment}`}
              className="text-blue-700 hover:underline font-semibold">
              {application?.hired_employment_number || `#${application?.hired_employment}`}
            </Link>.
            {' '}HR can take over from there.
          </div>
        </div>
      )}
      {!alreadyHired && offer.status === 'ACCEPTED' && (
        <div className="bg-green-50 border border-green-200 text-green-900 rounded-md p-4 text-sm flex items-center justify-between gap-4">
          <div>
            <span className="font-semibold">Offer accepted.</span> One click creates the Employment + Compensation rows,
            moves the application to the won stage, and (if the start date is today or in the past) triggers the
            Employee role automatically.
          </div>
          <Button
            onClick={() => convertMut.mutate()}
            disabled={!canHire || convertMut.isPending}
            title={!canHire ? 'Requires ats.hire' : undefined}
          >
            {convertMut.isPending ? 'Converting…' : 'Convert to Employment'}
          </Button>
        </div>
      )}
      <OfferCard
        offer={offer}
        canApprove={canApprove}
        onChanged={invalidate}
        confirm={confirm}
      />
    </div>
  )
}

function NoOfferYet({
  applicationId, candidateName, onCreated,
}: { applicationId: number; candidateName: string; onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center">
      <p className="text-sm text-muted-foreground mb-4">
        No offer drafted yet. Draft an offer when you're ready to extend a contract to {candidateName}.
      </p>
      <Button onClick={() => setOpen(true)}>
        <Plus className="h-3 w-3 mr-1" /> Draft offer
      </Button>
      <OfferDrawer
        open={open}
        onOpenChange={setOpen}
        applicationId={applicationId}
        editing={null}
        onSaved={() => { onCreated(); setOpen(false) }}
      />
    </div>
  )
}

function OfferCard({
  offer, canApprove, onChanged, confirm,
}: {
  offer: Offer
  canApprove: boolean
  onChanged: () => void
  confirm: (label: string, fn: () => void) => void
}) {
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)

  const actionMut = useMutation({
    mutationFn: async (action: string) =>
      (await api.post(`/ats/offers/${offer.id}/${action}/`)).data,
    onSuccess: () => {
      toast.success('Offer updated')
      onChanged()
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/ats/offers/${offer.id}/`),
    onSuccess: () => {
      toast.success('Offer deleted')
      qc.invalidateQueries({ queryKey: ['ats', 'offer', { application: offer.application }] })
      qc.invalidateQueries({ queryKey: ['ats', 'activities', { application: offer.application }] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const isDraft = offer.status === 'DRAFT'
  const isApproved = offer.status === 'APPROVED'
  const isSent = offer.status === 'SENT'
  const isAccepted = offer.status === 'ACCEPTED'
  const isTerminal = ['ACCEPTED', 'REJECTED', 'EXPIRED', 'WITHDRAWN'].includes(offer.status)

  // The "Offer accepted → convert" banner lives in OfferTab so it can wire the
  // button to the convert-to-employment action. OfferCard renders only the card.
  void isAccepted

  return (
    <>
      <div className="bg-card rounded-lg shadow">
        <div className="px-6 py-4 border-b flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold">Offer</h3>
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${OFFER_STATUS_COLOR[offer.status]}`}>
                {OFFER_STATUS_LABEL[offer.status]}
              </span>
              {offer.is_locked && (
                <span className="text-xs text-muted-foreground italic">terms locked — reopen to edit</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {offer.position_title && <span>Position: {offer.position_code} — {offer.position_title} · </span>}
              Start date: {offer.start_date}
              {offer.response_deadline && <span> · Respond by {offer.response_deadline}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isDraft && <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>Edit terms</Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm('this offer', () => deleteMut.mutate())}
                  className="text-destructive"
                >
                  Delete offer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field2 label="Salary">
            <span className="font-semibold">
              {Number(offer.salary_amount).toLocaleString()} {offer.currency_code}
            </span>
            <span className="text-muted-foreground"> / {FREQUENCY_LABEL[offer.salary_frequency].toLowerCase()}</span>
          </Field2>
          {offer.signing_bonus && (
            <Field2 label="Signing bonus">
              {Number(offer.signing_bonus).toLocaleString()} {offer.currency_code}
            </Field2>
          )}
          {offer.letter_url && (
            <Field2 label="Letter">
              <a href={offer.letter_url} target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline">Open letter</a>
            </Field2>
          )}
          {offer.sent_at && (
            <Field2 label="Sent">{new Date(offer.sent_at).toLocaleString()}</Field2>
          )}
          {offer.responded_at && (
            <Field2 label="Responded">{new Date(offer.responded_at).toLocaleString()}</Field2>
          )}
          {offer.approved_at && (
            <Field2 label="Approved">
              {new Date(offer.approved_at).toLocaleString()} by {offer.approved_by_username || '—'}
            </Field2>
          )}
          {offer.other_terms && (
            <Field2 label="Other terms" full>
              <div className="text-foreground whitespace-pre-wrap">{offer.other_terms}</div>
            </Field2>
          )}
        </div>

        {/* Workflow actions */}
        <div className="px-6 py-3 border-t bg-muted/20 flex items-center justify-end gap-2">
          {isDraft && (
            <Button
              size="sm"
              onClick={() => actionMut.mutate('approve')}
              disabled={!canApprove || actionMut.isPending}
              title={!canApprove ? 'Requires ats.approve_offer' : undefined}
            >
              Approve
            </Button>
          )}
          {isApproved && (
            <>
              <Button size="sm" variant="outline" onClick={() => actionMut.mutate('revoke')}>Revoke (back to draft)</Button>
              <Button size="sm" onClick={() => actionMut.mutate('send')}>Send</Button>
              <Button size="sm" variant="outline" onClick={() => actionMut.mutate('withdraw')}>Withdraw</Button>
            </>
          )}
          {isSent && (
            <>
              <Button size="sm" onClick={() => actionMut.mutate('accept')}>Mark accepted</Button>
              <Button size="sm" variant="outline" onClick={() => actionMut.mutate('reject')}>Mark rejected</Button>
              <Button size="sm" variant="outline" onClick={() => actionMut.mutate('expire')}>Mark expired</Button>
              <Button size="sm" variant="outline" onClick={() => actionMut.mutate('withdraw')}>Withdraw</Button>
            </>
          )}
          {isTerminal && (
            <Button size="sm" variant="outline" onClick={() => actionMut.mutate('reopen')}>Reopen to draft</Button>
          )}
        </div>
      </div>

      <OfferDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        applicationId={offer.application}
        editing={offer}
        onSaved={() => { onChanged(); setEditOpen(false) }}
      />
    </>
  )
}

function Field2({ label, children, full }: { label: string; children: ReactNode; full?: boolean }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function OfferDrawer({
  open, onOpenChange, applicationId, editing, onSaved,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  applicationId: number
  editing: Offer | null
  onSaved: () => void
}) {
  const today = () => new Date().toISOString().slice(0, 10)
  const initial = () => editing ? {
    salary_amount: editing.salary_amount,
    currency_code: editing.currency_code,
    salary_frequency: editing.salary_frequency,
    signing_bonus: editing.signing_bonus || '',
    start_date: editing.start_date,
    response_deadline: editing.response_deadline || '',
    other_terms: editing.other_terms,
    letter_url: editing.letter_url,
    position: editing.position,
  } : {
    salary_amount: '',
    currency_code: 'EUR',
    salary_frequency: 'ANNUAL' as Offer['salary_frequency'],
    signing_bonus: '',
    start_date: today(),
    response_deadline: '',
    other_terms: '',
    letter_url: '',
    position: null as number | null,
  }
  const [form, setForm] = useState(initial())
  useEffect(() => { if (open) setForm(initial()) /* eslint-disable-line */ }, [open, editing])

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: any = {
        application: applicationId,
        salary_amount: form.salary_amount,
        currency_code: form.currency_code,
        salary_frequency: form.salary_frequency,
        signing_bonus: form.signing_bonus === '' ? null : form.signing_bonus,
        start_date: form.start_date,
        response_deadline: form.response_deadline || null,
        other_terms: form.other_terms,
        letter_url: form.letter_url,
        position: form.position,
      }
      if (editing) {
        return (await api.patch(`/ats/offers/${editing.id}/`, payload)).data
      }
      return (await api.post('/ats/offers/', payload)).data
    },
    onSuccess: () => {
      toast.success(editing ? 'Offer updated' : 'Offer drafted')
      onSaved()
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit offer' : 'Draft offer'}
      description="Capture the financial terms. Once approved, comp fields lock — revoke or reopen to draft to change them."
      onSubmit={() => saveMut.mutate()}
      isPending={saveMut.isPending}
      submitLabel={editing ? 'Save' : 'Draft'}
    >
      <FieldGroup>
        <div className="grid grid-cols-3 gap-4">
          <Field className="col-span-2">
            <FieldLabel htmlFor="of-amt">Salary amount *</FieldLabel>
            <Input id="of-amt" type="number" step="0.01" min="0" required
              value={form.salary_amount}
              onChange={(e) => setForm({ ...form, salary_amount: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="of-cur">Currency</FieldLabel>
            <Input id="of-cur" maxLength={3} value={form.currency_code}
              onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })}
              className="font-mono uppercase" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="of-freq">Frequency</FieldLabel>
            <Select value={form.salary_frequency}
              onValueChange={(v) => setForm({ ...form, salary_frequency: v as Offer['salary_frequency'] })}>
              <SelectTrigger id="of-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(FREQUENCY_LABEL) as Offer['salary_frequency'][]).map((k) => (
                  <SelectItem key={k} value={k}>{FREQUENCY_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="of-bonus">Signing bonus</FieldLabel>
            <Input id="of-bonus" type="number" step="0.01" min="0"
              value={form.signing_bonus}
              onChange={(e) => setForm({ ...form, signing_bonus: e.target.value })}
              placeholder="Optional" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="of-start">Start date *</FieldLabel>
            <DatePicker id="of-start" required value={form.start_date}
              onChange={(v) => setForm({ ...form, start_date: v })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="of-deadline">Response deadline</FieldLabel>
            <DatePicker id="of-deadline" value={form.response_deadline}
              onChange={(v) => setForm({ ...form, response_deadline: v })} />
            <FieldDescription>Optional — informational in v1, no auto-expire.</FieldDescription>
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="of-url">Offer letter URL</FieldLabel>
          <Input id="of-url" type="url" value={form.letter_url}
            onChange={(e) => setForm({ ...form, letter_url: e.target.value })}
            placeholder="https://drive.google.com/…" />
          <FieldDescription>Link to the PDF / Google Doc. Letter is sent externally.</FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="of-terms">Other terms</FieldLabel>
          <Textarea id="of-terms" rows={4} value={form.other_terms}
            onChange={(e) => setForm({ ...form, other_terms: e.target.value })}
            placeholder="Equity, bonus eligibility, relocation, benefits, probation length, etc." />
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

// ─────────────────── Placeholder for future-phase tabs ───────────────────

function PhasePlaceholder({ phase, name }: { phase: string; name: string }) {
  return (
    <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center">
      <p className="text-sm text-muted-foreground">
        {name} arrive in <span className="font-semibold">Phase {phase}</span>. The data model + UI are not yet wired.
      </p>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
